import {useState, useCallback} from 'react';
import {useApiCall} from '@deezer/react-legacy-api';
import {useLegacyApiError} from '@app/hooks/useLegacyApiError';
import {useRedirection} from '@app/modules/redirection/hooks';
import {getDateFromAgeFormat} from '@app/utils/utils';
import {logClick} from '@app/modules/tracking/gtm';
import {useAuthVitalsMetrics} from '@app/hooks/metrics/useAuthVitalsMetrics';
import {useAutolog} from '@app/hooks/auth/useAutolog';
import {AuthStatus} from '@app/hooks/auth/useAutolog/utils';

export type FormValues =
	| {password?: string; birthday: string; sex?: string}
	| Record<string, never>;

export const useForm = (): {
	errorMessage: string;
	isLoading: boolean;
	onSubmit: (values: FormValues) => void;
} => {
	const [isLoading, setLoading] = useState(false);
	const {data, status} = useAutolog(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [getErrorMessage] = useLegacyApiError();
	const {logAuthError} = useAuthVitalsMetrics();
	const redirect = useRedirection();
	const apiCall = useApiCall();
	const onSubmit = useCallback(
		async (values: FormValues) => {
			setLoading(true);

			try {
				if (status !== AuthStatus.missinginfo) {
					return;
				}
				await apiCall({
					method: 'deezer.ping',
					data: {
						FORCE: true,
					},
				});

				const response: any = await apiCall({
					method: 'user.completeAccount',
					data: {
						ARL: data?.arl,
						PASSWORD: values.password,
						SEX: values.sex,
						BIRTHDAY: getDateFromAgeFormat(values.birthday),
					},
				});

				if (response?.arl) {
					logClick({
						eventaction: 'completed-profile-success',
					});
					setLoading(false);

					const redirect_uri = new URLSearchParams(window.location.search).get(
						'redirect_uri',
					);

					if (redirect_uri) {
						return (location.href = redirect_uri);
					}

					redirect();
				}
			} catch (error: any) {
				setLoading(false);
				setErrorMessage(getErrorMessage(error));
				logAuthError({
					e: error,
					journey_type: 'missing-info',
					gateway_method: `user.completeAccount`,
					payload: error,
				});
			}
		},
		[apiCall, getErrorMessage, redirect, logAuthError, data, status],
	);

	return {
		errorMessage,
		isLoading,
		onSubmit,
	};
};
